import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Truck, Check } from "lucide-react";
import { http } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/format";
import ProductCard from "../components/ProductCard";
import ProductReviews from "../components/ProductReviews";
import Reveal from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Skeleton } from "../components/ui/skeleton";

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, track } = useStore();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [pin, setPin] = useState("");
  const [pinRes, setPinRes] = useState(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");

  useEffect(() => {
    setP(null); setActiveImg(0); setSize(null); setPinRes(null);
    http.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setColor(r.data.colors?.[0]?.name || null);
      track("view_item", { product_id: r.data.id });
    }).catch(() => setP(false));
    http.get(`/products/${slug}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [slug, track]);

  if (p === false) return <div className="p-20 text-center text-zinc-500">Product not found.</div>;
  if (!p) return <div className="max-w-7xl mx-auto p-8 grid lg:grid-cols-2 gap-10"><Skeleton className="aspect-[3/4] bg-zinc-900" /><div className="space-y-4"><Skeleton className="h-10 w-2/3 bg-zinc-900" /><Skeleton className="h-6 w-1/3 bg-zinc-900" /></div></div>;

  const soldOut = !p.in_stock || p.availability === "Sold out";
  const sizeObj = (p.sizes || []).find((s) => s.size === size);
  const lowStock = sizeObj && sizeObj.stock > 0 && sizeObj.stock <= 5;

  const checkPin = async () => {
    try { const r = await http.get(`/pincode/${pin}`); setPinRes(r.data); }
    catch (e) { setPinRes({ error: e.response?.data?.detail || "Invalid PIN" }); }
  };

  const add = (buyNow) => {
    if ((p.sizes || []).length && !size) { toast.error("Please select a size"); return; }
    if (sizeObj && sizeObj.stock < qty) { toast.error("Not enough stock"); return; }
    addToCart({ product_id: p.id, name: p.name, slug: p.slug, image: p.images?.[0]?.url, size, color, qty, price: p.effective_price, mrp: p.mrp }, !buyNow);
    if (buyNow) navigate("/checkout");
  };

  const notifyMe = async (e) => {
    e.preventDefault();
    try { await http.post("/notify", { email: notifyEmail, product_id: p.id, size }); toast.success("We'll email you when it's back."); setNotifyOpen(false); setNotifyEmail(""); }
    catch { toast.error("Could not sign up"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-8 pb-28 lg:pb-8">
      <nav className="text-xs text-zinc-500 mb-6 flex gap-2">
        <Link to="/shop" className="hover:text-white">Shop</Link> / <span className="text-zinc-300">{p.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(p.images || []).map((im, i) => (
              <div key={i} className={`aspect-[3/4] overflow-hidden bg-zinc-950 ${i === 0 && p.images.length > 1 ? "sm:col-span-2" : ""}`}>
                <img src={im.url} alt={im.alt || p.name} className="w-full h-full object-cover" data-testid={`pdp-image-${i}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5 lg:sticky lg:top-24 h-fit space-y-5">
          <div className="flex gap-2 flex-wrap">
            {(p.labels || []).map((l, i) => (
              <span key={i} className="text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider text-white" style={{ background: "var(--ss-accent)" }}>{l.text}</span>
            ))}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white uppercase tracking-tight leading-none">{p.name}</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-2xl font-bold text-white" data-testid="pdp-price">{inr(p.effective_price)}</span>
            {p.discount_pct > 0 && <span className="text-zinc-500 line-through">{inr(p.mrp)}</span>}
            {p.discount_pct > 0 && <span className="text-xs font-bold px-2 py-0.5 text-white" style={{ background: "var(--ss-accent)" }}>{p.discount_pct}% OFF</span>}
          </div>
          <p className="text-xs text-zinc-500">Inclusive of all taxes (GST {p.gst_rate || 5}%)</p>
          <p className="text-sm text-zinc-400 leading-relaxed">{p.short_description}</p>

          {/* Colors */}
          {p.colors?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2">Colour: <span className="text-white">{color}</span></p>
              <div className="flex gap-2">
                {p.colors.map((c) => (
                  <button key={c.name} onClick={() => setColor(c.name)} title={c.name}
                    className={`w-8 h-8 border-2 ${color === c.name ? "border-accent-ss" : "border-zinc-700"}`}
                    style={{ background: c.hex }} data-testid={`pdp-color-${c.name}`} />
                ))}
              </div>
            </div>
          )}

          {/* Sizes */}
          {p.sizes?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2">Size</p>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => {
                  const out = (s.stock || 0) <= 0;
                  return (
                    <button key={s.size} disabled={out} onClick={() => setSize(s.size)}
                      className={`min-w-[48px] h-12 px-2 border text-sm relative ${size === s.size ? "border-accent-ss text-accent" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"} ${out ? "text-zinc-700 cursor-not-allowed" : ""}`}
                      data-testid={`pdp-size-${s.size}`}>
                      <span className={out ? "line-through" : ""}>{s.size}</span>
                    </button>
                  );
                })}
              </div>
              {lowStock && <p className="text-xs text-amber-400 mt-2">Only {sizeObj.stock} left in {size}!</p>}
            </div>
          )}

          {/* Quantity + add */}
          {!soldOut ? (
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-zinc-800">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 text-zinc-300" aria-label="Decrease"><Minus size={14} /></button>
                  <span className="px-4 text-sm">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="px-3 py-3 text-zinc-300" aria-label="Increase"><Plus size={14} /></button>
                </div>
                <span className="text-xs text-green-400 flex items-center gap-1"><Check size={14} /> In stock</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => add(false)} className="btn-accent py-4 text-sm font-bold uppercase tracking-wider" data-testid="pdp-add-to-cart-button">Add to Cart</button>
                <button onClick={() => add(true)} className="border border-zinc-700 py-4 text-sm font-bold uppercase tracking-wider text-white hover:border-white" data-testid="pdp-buy-now-button">Buy Now</button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-red-400 font-semibold uppercase text-sm tracking-wider">Sold Out</p>
              {!notifyOpen ? (
                <button onClick={() => setNotifyOpen(true)} className="border border-zinc-700 w-full py-4 text-sm font-bold uppercase tracking-wider text-white" data-testid="pdp-notify-open">Notify Me When Back</button>
              ) : (
                <form onSubmit={notifyMe} className="flex">
                  <input type="email" required value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="your@email.com"
                    data-testid="pdp-notify-input" className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-3 text-sm" />
                  <button className="btn-accent px-5 text-xs font-bold uppercase" data-testid="pdp-notify-submit">Notify</button>
                </form>
              )}
            </div>
          )}

          {/* PIN check */}
          <div className="bg-zinc-900/60 border border-zinc-800 p-4 space-y-2">
            <p className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-2"><Truck size={15} /> Delivery & COD</p>
            <div className="flex">
              <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter 6-digit PIN code"
                data-testid="pdp-pincode-input" className="flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm" />
              <button onClick={checkPin} className="btn-accent px-5 text-xs font-bold uppercase" data-testid="pdp-pincode-check-btn">Check</button>
            </div>
            {pinRes && (pinRes.error ? <p className="text-xs text-red-400">{pinRes.error}</p> :
              <p className="text-xs text-green-400">Delivery in {pinRes.estimated_days} days. {pinRes.cod_available ? "Cash on Delivery available." : "Prepaid only."}</p>)}
          </div>

          {/* Accordions */}
          <Accordion type="single" collapsible className="border-t border-zinc-800">
            <AccordionItem value="desc" className="border-zinc-800">
              <AccordionTrigger className="text-sm uppercase tracking-wider">Description</AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-400 leading-relaxed">{p.long_description}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="fabric" className="border-zinc-800">
              <AccordionTrigger className="text-sm uppercase tracking-wider">Fabric & Care</AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-400">{p.fabric}<br />{p.care}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-zinc-800">
              <AccordionTrigger className="text-sm uppercase tracking-wider">Shipping & Returns</AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-400">Free shipping over ₹1999. 7-day easy returns. Cash on Delivery available pan-India.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-wide mb-6">You May Also Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            {related.map((r, i) => <ProductCard key={r.id} p={r} index={i} />)}
          </div>
        </section>
      )}

      <ProductReviews slug={p.slug} productId={p.id} />

      {/* Mobile sticky bar */}
      {!soldOut && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#09090b] border-t border-zinc-800 p-3 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-bold text-white">{inr(p.effective_price)}</p>
            <p className="text-[10px] text-zinc-500">{size ? `Size ${size}` : "Select size"}</p>
          </div>
          <button onClick={() => add(false)} className="btn-accent flex-1 py-3.5 text-sm font-bold uppercase tracking-wider" data-testid="pdp-sticky-add">Add to Cart</button>
        </div>
      )}
    </div>
  );
}
